/**
 * Pure gameplay formulas. Every formula reads its constants from GameConfig so
 * the relationships (size<->speed, size<->mass, eat threshold) are tuned in one
 * place and unit-tested in isolation.
 */
import type { GameConfig } from '../config/config';
import { clamp } from './math/vec2';
import type { Entity } from './types';

/** Mass is proportional to area (size = radius). The constant pi is irrelevant
 *  and dropped, so mass = size^2. */
export function sizeToMass(size: number): number {
  return size * size;
}

export function massToSize(mass: number): number {
  return Math.sqrt(Math.max(0, mass));
}

/**
 * Size -> movement speed. Bigger is slower, with a floor so the largest blobs
 * still creep. speed = baseSpeed * (referenceSize / size)^exponent, clamped to
 * [minSpeed, baseSpeed].
 */
export function sizeToSpeed(size: number, cfg: GameConfig): number {
  const m = cfg.movement;
  const raw = m.baseSpeed * Math.pow(m.referenceSize / size, m.sizeSpeedExponent);
  return clamp(raw, m.minSpeed, m.baseSpeed);
}

/**
 * Can `attackerSize` eat `preySize` given their center distance?
 * Requires a size advantage AND a deep-enough overlap.
 */
export function canEat(
  attackerSize: number,
  preySize: number,
  distance: number,
  cfg: GameConfig,
): boolean {
  if (attackerSize < preySize * cfg.eat.sizeRatio) return false;
  return distance <= attackerSize - preySize * cfg.eat.overlapFactor;
}

/** New size after `attackerSize` eats `preySize`, conserving (converted) mass.
 *  Eating stays fully area-conserving (no diminishing returns) so hunting
 *  remains the fast path; the result is still clamped to maxSize by the caller. */
export function sizeAfterEating(attackerSize: number, preySize: number, cfg: GameConfig): number {
  const gainedMass = sizeToMass(preySize) * cfg.eat.massConversion;
  return massToSize(sizeToMass(attackerSize) + gainedMass);
}

/**
 * Diminishing-returns multiplier for grow clicks. ~1 when small, fading to 0 as
 * size approaches maxSize, shaped by actions.grow.diminishingExponent. This is
 * what makes clicking punchy early and a grind near the cap.
 *   progress = (size - minSize) / (maxSize - minSize), clamped to [0, 1]
 *   falloff  = (1 - progress)^diminishingExponent
 */
export function growthFalloff(size: number, cfg: GameConfig): number {
  const { minSize, maxSize } = cfg.world;
  const span = Math.max(1e-6, maxSize - minSize);
  const progress = clamp((size - minSize) / span, 0, 1);
  return Math.pow(1 - progress, cfg.actions.grow.diminishingExponent);
}

/** The inputs to a run score. Kept explicit so it is trivially unit-testable. */
export interface ScorableStats {
  /** Largest radius reached this life. */
  peakSize: number;
  /** Total prey mass eaten this life. */
  massEaten: number;
  /** Number of prey eaten this life. */
  kills: number;
  /** Seconds the entity has been alive. */
  survivalSeconds: number;
}

/**
 * Run score: peak mass plus continuous factors (mass eaten, kills, time alive).
 * The continuous terms keep the leaderboard ordered even when several blobs sit
 * at the size cap (so maxed-out players don't tie). All weights are config-driven.
 */
export function computeScore(stats: ScorableStats, cfg: GameConfig): number {
  const s = cfg.scoring;
  return Math.floor(
    s.peakMassWeight * sizeToMass(stats.peakSize) +
      s.massEatenWeight * stats.massEaten +
      s.killWeight * stats.kills +
      s.survivalWeight * stats.survivalSeconds,
  );
}

/** Convenience: score an entity at the given simulation time `now`. */
export function entityScore(entity: Entity, now: number, cfg: GameConfig): number {
  return computeScore(
    {
      peakSize: entity.peakSize,
      massEaten: entity.massEaten,
      kills: entity.kills,
      survivalSeconds: Math.max(0, now - entity.bornAt),
    },
    cfg,
  );
}
